import { Box, Tooltip, Typography } from '@mui/material';

import { AnalysisStatus } from '../../../../../../../api/theHive/licensedCompanies/types';
import {
  IconCancelMS,
  IconCheckCircleMS,
  IconPendingMS,
} from '../../../../../../../constants/icons';
// `formatOnlyDate` e não `formatDate`: o campo é datetime, e `formatDate` só
// sabe partir `YYYY-MM-DD`.
import { formatOnlyDate } from '../../../../../../../helpers';

// O eixo administrativo é o contrato de formalização, não um conjunto de
// documentos de análise — os textos do tooltip mudam para não mentir.
type AnalysisAxis = 'documents' | 'contract';

interface AnalysisStatusIconProps {
  status: AnalysisStatus;
  axis?: AnalysisAxis;
  // `formalization_submitted_at` do payload. Só o eixo do contrato usa: em
  // `pending`, é o que separa "esperando o banco" de "esperando o credenciado".
  submittedAt?: string | null;
  pendingScopeTerms?: number;
}

const ICONS: Record<
  Exclude<AnalysisStatus, 'na'>,
  { icon: JSX.Element; color: string }
> = {
  approved: { icon: IconCheckCircleMS, color: '#2E7D32' },
  rejected: { icon: IconCancelMS, color: '#C62828' },
  pending: { icon: IconPendingMS, color: '#ED6C02' },
};

const LABELS: Record<AnalysisAxis, Record<AnalysisStatus, string>> = {
  documents: {
    approved: 'Todos os documentos aprovados',
    rejected: 'Há documento reprovado',
    pending: 'Há documento a analisar',
    na: 'Sem documentos neste eixo',
  },
  contract: {
    approved: 'Contrato aprovado',
    rejected: 'Contrato reprovado',
    pending: 'Contrato a enviar ou a analisar',
    na: 'Fora da etapa de formalização',
  },
};

export function AnalysisStatusIcon({
  status,
  axis = 'documents',
  submittedAt,
  pendingScopeTerms = 0,
}: AnalysisStatusIconProps): JSX.Element {
  // `pending` no contrato cobre dois estados opostos: submetido (a fila está
  // parada no banco) e não submetido (parada no credenciado). Com a data, o
  // tooltip diz de quem é a vez.
  const contractPendingLabel = (): string => {
    if (pendingScopeTerms > 0) return 'Termo aditivo de escopo a analisar';
    if (!submittedAt) return 'Contrato ainda não enviado';
    return `Contrato enviado em ${formatOnlyDate(
      submittedAt
    )}, aguardando análise`;
  };

  const isContractPending = axis === 'contract' && status === 'pending';
  const label = isContractPending
    ? contractPendingLabel()
    : LABELS[axis][status];

  if (status === 'na') {
    return (
      <Tooltip title={label}>
        <Typography color="text.secondary">—</Typography>
      </Tooltip>
    );
  }

  const { icon, color } = ICONS[status];
  return (
    <Tooltip title={label}>
      <Box
        component="span"
        sx={{
          color,
          display: 'inline-flex',
          '& svg': { fontSize: 24 },
        }}
      >
        {icon}
      </Box>
    </Tooltip>
  );
}
